import { Injectable } from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  Subject,
  switchMap,
} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment, HttpHeadersService } from '@perval/shared';
import { ErrorStateService } from '../state/error-state.service';
import { GeoInfoReadHttp } from '../models/geo-info-read-http.dto';
import { geoInfoReadFromGeoInfoReadHttp } from './mappers/geo-info-read.mapper';
import { addressesFromAddressesHttp } from './mappers/address-data.mapper';
import { AddressHttp } from '../models/address-http.dto';
import { Address } from '../models/address.model';

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  mainUrl = `${environment.baseApiUrl}`;

  addressInputAutoCompletion$ = new Subject<string>();
  readonly DEBOUNCE_BEFORE_API_CALL_MS = 500;
  readonly STATUS_OF_AUTOCOMPLETION_ERROR_FOR_WRONG_REQUEST = 400;
  readonly STATUS_OF_AUTOCOMPLETION_ERROR_TO_DISPLAY = 503;
  readonly MINIMUM_ADDRESS_INPUT_LENGTH = 3;
  readonly MAXIMUM_ADDRESS_INPUT_LENGTH = 200;

  constructor(
    private readonly http: HttpClient,
    private readonly httpHeadersServiceService: HttpHeadersService,
    private readonly errorService: ErrorStateService,
  ) {}

  /**
   * récupère l'autocompletion via getAddressAutocomplete si ca fait plus de x millisecondes qu'il n'y a pas eu de nouvelle frappes et que l'entrée est différente
   * de la précédente enlève aussi les espaces de l'adresse
   * @returns
   */
  getAddressInputAutoCompletion() {
    return this.addressInputAutoCompletion$.asObservable().pipe(
      debounceTime(this.DEBOUNCE_BEFORE_API_CALL_MS),
      distinctUntilChanged(),
      map((address) => (address ?? '').trim()),
      switchMap((addressInput: string) => {
        return this.isAddressInputLengthLongEnoughForAutoComplete(addressInput)
          ? this.getAddressAutocomplete(addressInput)
          : of([]);
      }),
    );
  }

  /**
   * permet de transmettre l'addresse dont on veut récupérer l'autocomplétion
   */
  setAddressInputAutoCompletion(addressInput: string) {
    this.addressInputAutoCompletion$.next(addressInput);
  }

  /**
   * permet de récupérer les détails d'une adresse à partir des informations de celle-ci (utile lorsqu'on choisi une adresse dans l'autocomplétion)
   * @param address
   * @returns
   */
  async getAddressInputFromAddressDetails(address: Address) {
    const params = new HttpParams()
      .set('city_code', address.cityCode)
      .set('x', address.x)
      .set('y', address.y);

    const response = await firstValueFrom(
      this.http
        .get<GeoInfoReadHttp>(`${this.mainUrl}/perval-geo/geo/info`, {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
          params,
        })
        .pipe(map(geoInfoReadFromGeoInfoReadHttp)),
    );

    return response;
  }

  /**
   * appel api qui permet de récupérer l'autocomplétion, émet une erreur dans le state error en cas d'erreur 503 avec un message personnalisé
   */
  private async getAddressAutocomplete(
    addressValue: string,
    limit: number = 5,
  ) {
    const params = new HttpParams()
      .set('q', addressValue)
      .set('l', limit.toString());

    this.errorService.clearError();

    return await firstValueFrom(
      this.http
        .get<{ items: AddressHttp[] }>(
          `${this.mainUrl}/perval-geo/addresses/autocomplete`,
          {
            headers:
              await this.httpHeadersServiceService.getHeadersWithPervalContext(),
            params,
          },
        )
        .pipe(
          map(addressesFromAddressesHttp),
          catchError((err) => {
            if (
              err.status ===
                this.STATUS_OF_AUTOCOMPLETION_ERROR_FOR_WRONG_REQUEST ||
              err.status === this.STATUS_OF_AUTOCOMPLETION_ERROR_TO_DISPLAY
            ) {
              this.errorService.setError(
                err.error.errors[0].message,
                true,
                err.status,
              );
            }
            return of([]);
          }),
        ),
    );
  }

  private isAddressInputLengthLongEnoughForAutoComplete(addressInput: string) {
    return (
      addressInput.length >= this.MINIMUM_ADDRESS_INPUT_LENGTH &&
      addressInput.length <= this.MAXIMUM_ADDRESS_INPUT_LENGTH
    );
  }
}
